import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { ProgressBar } from '../components/ProgressBar';
import { SurveyOption } from '../components/SurveyOption';
import { PhotoOption } from '../components/PhotoOption';
import { Footer } from '../components/Footer';
import { Lock, CheckCircle2, Check } from 'lucide-react';
// Survey Data Structure
const questions = [{
  id: 1,
  text: 'Soy...',
  step: 1,
  type: 'photo' as const,
  showIntro: true,
  options: [{
    label: 'Hombre',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=600&fit=crop',
    gradient: 'from-blue-600/70 to-indigo-900/70'
  }, {
    label: 'Mujer',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=600&fit=crop',
    gradient: 'from-pink-600/70 to-purple-900/70'
  }]
}, {
  id: 2,
  text: '¿Cuántos años tienes?',
  step: 1,
  type: 'emoji' as const,
  options: [{
    emoji: '🌱',
    text: 'Menos de 18'
  }, {
    emoji: '🌟',
    text: '18-25'
  }, {
    emoji: '✨',
    text: '25-40'
  }, {
    emoji: '🌺',
    text: '40+'
  }]
}, {
  id: 3,
  text: '¿Crees en la espiritualidad?',
  step: 1,
  type: 'emoji' as const,
  options: [{
    emoji: '✅',
    text: 'Sí'
  }, {
    emoji: '❌',
    text: 'No'
  }, {
    emoji: '🤷',
    text: 'No estoy seguro/a'
  }]
}, {
  id: 4,
  text: '¿Estás satisfecho/a con cómo van las cosas en tu vida?',
  step: 2,
  type: 'emoji' as const,
  options: [{
    emoji: '✅',
    text: 'Sí'
  }, {
    emoji: '❌',
    text: 'No'
  }, {
    emoji: '🤷',
    text: 'No estoy seguro/a'
  }]
}, {
  id: 5,
  text: '¿Alguna vez has recibido una lectura psíquica?',
  step: 2,
  type: 'emoji' as const,
  options: [{
    emoji: '✅',
    text: 'Sí'
  }, {
    emoji: '❌',
    text: 'No'
  }]
}];
const loadingSteps = [{
  text: 'Analizando tus respuestas',
  delay: 0
}, {
  text: 'Buscando una lista de psíquicos disponibles',
  delay: 1500
}, {
  text: 'Conectándote con los mejores psíquicos',
  delay: 3000
}, {
  text: 'Personalizando tu experiencia',
  delay: 4500
}];
export function SurveyPage() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [direction, setDirection] = useState(1);
  const [showLoading, setShowLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const showIntro = currentQuestion?.showIntro;
  useEffect(() => {
    if (showLoading) {
      // Animate progress from 0 to 100
      const duration = 6000; // 6 seconds
      const steps = 100;
      const interval = duration / steps;
      let currentProgress = 0;
      const timer = setInterval(() => {
        currentProgress += 1;
        setProgress(currentProgress);
        if (currentProgress >= 100) {
          clearInterval(timer);
          // Show the continue button when progress reaches 100
          setTimeout(() => {
            setShowContinueButton(true);
          }, 500);
        }
      }, interval);
      // Mark steps as completed
      loadingSteps.forEach((step, index) => {
        setTimeout(() => {
          setCompletedSteps(prev => [...prev, index]);
        }, step.delay);
      });
      return () => clearInterval(timer);
    }
  }, [showLoading]);
  const handleOptionClick = (optionValue: string) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionValue
    }));
    if (!isLastQuestion) {
      setDirection(1);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 300);
    } else {
      // Show loading screen after last question
      setDirection(1);
      setTimeout(() => {
        setShowLoading(true);
      }, 300);
    }
  };
  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setDirection(-1);
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -50 : 50,
      opacity: 0
    })
  };
  return <>
      {/* Continue Button Overlay - Always rendered from the start, hidden until ready */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: showContinueButton ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 flex items-center justify-center z-50 bg-black/20" style={{
      pointerEvents: showContinueButton ? 'auto' : 'none'
    }} data-id="element-136">
        <motion.a href="https://trk.topreviewspicks.com/click" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: showContinueButton ? 1 : 0.9,
        opacity: showContinueButton ? 1 : 0
      }} transition={{
        delay: showContinueButton ? 0.2 : 0,
        duration: 0.3
      }} className="px-10 py-3 bg-[#B45092] text-white font-semibold text-3xl rounded-full shadow-lg hover:bg-[#9d4280] transition-all duration-300 hover:shadow-xl" data-id="element-137">
          Continuar
        </motion.a>
      </motion.div>

      {showLoading ? <div className="min-h-screen w-full bg-[#EDF1FF] text-[#121212] overflow-hidden flex flex-col relative" data-id="element-138">
          <Header onBack={() => {}} showBack={false} data-id="element-139" />

          <main className={`flex-1 flex flex-col items-center justify-center px-4 max-w-2xl mx-auto w-full transition-all duration-500 ${showContinueButton ? 'blur-sm' : ''}`} data-id="element-140">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex flex-col items-center text-center w-full" data-id="element-141">
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-[#121212]" data-id="element-142">
                <span className="text-[#121212]/70" data-id="element-143">Perfecto, todo listo.</span>
                <br data-id="element-144" />
                Ahora estamos creando tu perfil
              </h2>

              {/* Circular Progress */}
              <div className="relative w-48 h-48 my-12" data-id="element-145">
                <svg className="w-full h-full transform -rotate-90" data-id="element-146">
                  {/* Background circle */}
                  <circle cx="96" cy="96" r="88" stroke="#B45092" strokeWidth="8" fill="none" opacity="0.2" data-id="element-147" />
                  {/* Progress circle */}
                  <motion.circle cx="96" cy="96" r="88" stroke="#B45092" strokeWidth="8" fill="none" strokeLinecap="round" initial={{
                strokeDasharray: '0 552'
              }} animate={{
                strokeDasharray: `${progress / 100 * 552} 552`
              }} transition={{
                duration: 0.1
              }} data-id="element-148" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-149">
                  <span className="text-5xl font-bold text-[#121212]" data-id="element-150">
                    {progress}%
                  </span>
                </div>
              </div>

              {/* Loading Steps */}
              <div className="w-full max-w-md space-y-3" data-id="element-151">
                {loadingSteps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: step.delay / 1000
            }} className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 ${completedSteps.includes(index) ? 'bg-[#B45092]/20 border border-[#B45092]' : 'bg-[#121212]/5 border border-transparent'}`} data-id="element-152">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${completedSteps.includes(index) ? 'bg-[#B45092] text-white' : 'bg-[#121212]/20 text-[#121212]'}`} data-id="element-153">
                      {completedSteps.includes(index) && <Check className="w-4 h-4" data-id="element-154" />}
                    </div>
                    <span className={`text-sm md:text-base font-medium transition-colors duration-300 ${completedSteps.includes(index) ? 'text-[#121212]' : 'text-[#121212]/70'}`} data-id="element-155">
                      {step.text}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
          </main>

          <Footer data-id="element-156" />
        </div> : <div className="min-h-screen w-full bg-[#EDF1FF] text-[#121212] overflow-hidden flex flex-col" data-id="element-157">
          <Header onBack={handleBack} showBack={currentQuestionIndex > 0} data-id="element-158" />

          <main className="flex-1 flex flex-col items-center px-2 pt-4 pb-4 max-w-2xl mx-auto w-full" data-id="element-159">
            {/* Progress Section - Hide on intro screen */}
            {!showIntro && currentQuestion && <div className="w-full mb-2" data-id="element-160">
                <ProgressBar currentQuestion={currentQuestionIndex + 1} totalQuestions={26} data-id="element-161" />
              </div>}

            {/* Content Section */}
            <div className="w-full relative min-h-[400px]" data-id="element-162">
              <AnimatePresence mode="wait" custom={direction} data-id="element-163">
                <motion.div key={currentQuestionIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} className="w-full" data-id="element-164">
                  {/* Intro Content - Only on first screen */}
                  {showIntro && <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5
              }} className="flex flex-col items-center text-center" data-id="element-165">
                      <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight text-[#121212]" data-id="element-166">
                        Lecturas precisas y personalizadas
                        <br data-id="element-167" />
                        de los mejores psíquicos
                      </h1>

                      <p className="text-[#121212]/70 text-base md:text-lg mb-6 max-w-xl" data-id="element-168">
                        Comienza con nuestro cuestionario de 5 pre para
                        conectarte con el psíquico que mejor se adapte a tus
                        necesidades
                      </p>
                    </motion.div>}

                  <h2 className="text-2xl md:text-2xl font-bold text-center mb-8 px-4 leading-relaxed text-[#121212]" data-id="element-169">
                    {currentQuestion.text}
                  </h2>

                  {currentQuestion.type === 'photo' ? <div className="flex flex-wrap items-center justify-center gap-6 w-full" data-id="element-170">
                      {currentQuestion.options.map((option, idx) => <motion.div key={option.label} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.1
                }} data-id="element-171">
                          <PhotoOption image={option.image} label={option.label} gradient={option.gradient} onClick={() => handleOptionClick(option.label)} selected={answers[currentQuestion.id] === option.label} data-id="element-172" />
                        </motion.div>)}
                    </div> : <div className="flex flex-col gap-1 w-full" data-id="element-173">
                      {currentQuestion.options.map((option, idx) => <motion.div key={option.text} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.05
                }} data-id="element-174">
                          <SurveyOption emoji={option.emoji!} text={option.text!} onClick={() => handleOptionClick(option.text!)} selected={answers[currentQuestion.id] === option.text} data-id="element-175" />
                        </motion.div>)}
                    </div>}

                  {currentQuestion.type === 'photo' && <div className="mt-8 flex flex-col items-center" data-id="element-176">
                      <p className="text-center text-sm text-[#121212]/70 max-w-md mx-auto mb-4" data-id="element-177">
                        Nuestro algoritmo te conectará con el mejor psíquico que
                        te brindará orientación y las respuestas más precisas.
                      </p>

                      <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-178">
                        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#B45092]/30 shadow-sm" data-id="element-179">
                          <Lock className="w-4 h-4 text-[#B45092]" data-id="element-180" />
                          <span className="text-sm text-[#121212] font-medium" data-id="element-181">
                            Privacidad garantizada
                          </span>
                        </div>

                        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#B45092]/30 shadow-sm" data-id="element-182">
                          <CheckCircle2 className="w-4 h-4 text-[#B45092]" data-id="element-183" />
                          <span className="text-sm text-[#121212] font-medium" data-id="element-184">
                            Psíquicos verificados
                          </span>
                        </div>
                      </div>
                    </div>}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>

          <Footer data-id="element-185" />
        </div>}
    </>;
}